import { computed, watch, type Ref } from "vue";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { useReportsStore } from "@/_store/reports.module";
import { useFilters } from "@/composables/useFilters";
import {
  REPORT_USERS_SEARCH_DEBOUNCE,
  REPORT_USERS_SEARCH_MIN_LENGTH,
} from "@/constants/reports.ts";

type ReportUsersFilterContext =
  FilterContext.PHISHING_SIMULATION_REPORT_USERS | FilterContext.SECURITY_AWARENESS_REPORT_USERS;

interface UseReportUsersSearchOptions<T> {
  context: ReportUsersFilterContext;
  reportUsers: Ref<T[]>;
  searchedUsers: Ref<T[]>;
  searchUsers: () => Promise<void>;
}

/**
 * Search over the users of a SAT report. The report response only carries the top users it displays,
 * so anything typed into the search box is resolved by the BE against all enrolled users.
 * Below REPORT_USERS_SEARCH_MIN_LENGTH characters the report's own user list is shown unchanged.
 */
export function useReportUsersSearch<T>({
  context,
  reportUsers,
  searchedUsers,
  searchUsers,
}: UseReportUsersSearchOptions<T>) {
  const filtersStore = useFiltersStore();
  const reportsStore = useReportsStore();

  const isSearchActive = computed(
    () => (filtersStore.filters[context].search?.length ?? 0) >= REPORT_USERS_SEARCH_MIN_LENGTH
  );

  const displayedUsers = computed<T[]>(() =>
    isSearchActive.value ? searchedUsers.value : reportUsers.value
  );

  const { localFilters } = useFilters({
    context,
    searchKey: "search",
    searchMinLength: REPORT_USERS_SEARCH_MIN_LENGTH,
    debounceMs: REPORT_USERS_SEARCH_DEBOUNCE,
    onFilterChange: async () => {
      if (isSearchActive.value) {
        await searchUsers();
      }
    },
  });

  // The report is refetched when the time frame changes, so an active search has to be rerun
  // against the new period instead of keeping results from the previous one. The tab sets the
  // time frame on mount as well, which is what runs a search restored from the persisted filters.
  watch(
    () => reportsStore.selectedTimeFrame,
    async () => {
      if (isSearchActive.value) {
        await searchUsers();
      }
    }
  );

  return {
    userSearchFilters: localFilters,
    displayedUsers,
  };
}
